import * as BannerImpressionTracker from "@omni/services/tracking/trackers/impression/bannerImpression.tracker";
import * as BannerSlideImpressionTracker from "@omni/services/tracking/trackers/impression/bannerSlideImpression.tracker";
import * as ButtonImpressionTracker from "@omni/services/tracking/trackers/impression/buttonImpression.tracker";
import * as CartImpressionTracker from "@omni/services/tracking/trackers/impression/cartImpression.tracker";
import * as ListImpressionTracker from "@omni/services/tracking/trackers/impression/listImpression.tracker";
import * as PageImpressionTracker from "@omni/services/tracking/trackers/impression/pageImpression.tracker";
import * as ProductDetailImpressionTracker from "@omni/services/tracking/trackers/impression/productDetailImpression.tracker";
import * as ProductImpressionTracker from "@omni/services/tracking/trackers/impression/productImpression.tracker";
import * as ReviewImpressionTracker from "@omni/services/tracking/trackers/impression/reviewImpression.tracker";
import * as SectionImpressionTracker from "@omni/services/tracking/trackers/impression/sectionImpression.tracker";
import { EventType } from "@omni/types/components/tracking/eventType.enum";
import { TrackingEvent } from "@omni/types/components/tracking/trackingEvent.type";
import { TrackingProps } from "@omni/types/components/tracking/trackingProps.type";
import { isDefined } from "@omni/utils/helper.utils";
import { RefObject, useCallback, useMemo, useRef } from "react";
import { IntersectionOptions, useInView } from "react-intersection-observer";

import { toTrackingEvents } from "./helpers/trackingEvent.helpers";

const toCallback =
    (elementRef: RefObject<HTMLElement | null>) => (event: TrackingEvent) => {
        if (!isDefined(event)) return;
        if (!isDefined(event.trackingInfo)) return;

        switch (event.type) {
            case EventType.BannerImpression: {
                const trackingInfo = event.trackingInfo;
                return () => BannerImpressionTracker.track(trackingInfo);
            }
            case EventType.BannerSlideImpression: {
                const trackingInfo = event.trackingInfo;
                return () => BannerSlideImpressionTracker.track(trackingInfo);
            }
            case EventType.ButtonImpression: {
                const trackingInfo = event.trackingInfo;
                const element = elementRef.current;
                if (!isDefined(element)) return;

                return () =>
                    ButtonImpressionTracker.track(trackingInfo, element);
            }
            case EventType.CartImpression: {
                const trackingInfo = event.trackingInfo;
                return () => CartImpressionTracker.track(trackingInfo);
            }
            case EventType.ListImpression: {
                const trackingInfo = event.trackingInfo;
                return () => ListImpressionTracker.track(trackingInfo);
            }
            case EventType.PageImpression: {
                return () => PageImpressionTracker.track();
            }
            case EventType.ProductDetailImpression: {
                const trackingInfo = event.trackingInfo;
                return () => ProductDetailImpressionTracker.track(trackingInfo);
            }
            case EventType.ProductImpression: {
                const trackingInfo = event.trackingInfo;
                return () => ProductImpressionTracker.track(trackingInfo);
            }
            case EventType.ReviewImpression: {
                const trackingInfo = event.trackingInfo;
                return () => ReviewImpressionTracker.track(trackingInfo);
            }
            case EventType.SectionImpression: {
                const trackingInfo = event.trackingInfo;
                return () => SectionImpressionTracker.track(trackingInfo);
            }
        }
    };

export type UseImpressionTrackingIntersectionOptions = Omit<
    IntersectionOptions,
    "onChange" | "triggerOnce"
>;

export const useImpressionTracking = (
    trackingProps: TrackingProps | undefined,
    intersectionOptions?: UseImpressionTrackingIntersectionOptions
) => {
    const elementRef = useRef<HTMLElement>(null);

    const trackingEvents = useMemo(() => {
        return toTrackingEvents(trackingProps ?? {});
    }, [trackingProps]);

    const eventCallbacks = useMemo(() => {
        return trackingEvents.map(toCallback(elementRef)).filter(isDefined);
    }, [trackingEvents]);

    const onInViewChange = useCallback(
        (inView: boolean) => {
            if (!inView) return;

            eventCallbacks.forEach((callback) => callback());
        },
        [eventCallbacks]
    );

    const { ref: inViewRef, inView } = useInView({
        triggerOnce: true,
        onChange: onInViewChange,
        ...intersectionOptions
    });

    const ref = (node: HTMLElement | null) => {
        inViewRef(node);
        elementRef.current = node;
    };

    return { inView, ref };
};
